import { z } from 'zod';
import type { FieldDescriptor } from '@/types/fieldDescriptor';
import {
  heightFtZod,
  heightInchesZod,
  weightKgZod,
} from '@/utils/heightWeightZod';

export interface BuildFieldGroupSchemaOptions {
  superRefine?: (data: Record<string, unknown>, ctx: z.RefinementCtx) => void;
}

function applyStringValidation(
  base: z.ZodString,
  validation: FieldDescriptor['validation'],
  fieldType: FieldDescriptor['fieldType'],
): z.ZodTypeAny {
  let schema: z.ZodTypeAny = base;

  if (fieldType === 'EMAIL') {
    schema = (schema as z.ZodString).trim().email(
      validation.errorMessage ?? 'Please enter a valid email address',
    );
    return schema;
  }

  if (fieldType === 'PHONE') {
    let phoneSchema = (schema as z.ZodString);
    if (validation.required) {
      phoneSchema = phoneSchema.min(
        validation.minLength ?? 10,
        validation.errorMessage ?? 'Phone number must be exactly 10 digits',
      );
    }
    phoneSchema = phoneSchema.max(
      validation.maxLength ?? 10,
      validation.errorMessage ?? 'Phone number must be exactly 10 digits',
    );
    if (validation.pattern) {
      phoneSchema = phoneSchema.regex(
        new RegExp(validation.pattern),
        validation.patternMessage ?? validation.errorMessage,
      );
    }
    return phoneSchema;
  }

  let stringSchema = schema as z.ZodString;
  if (validation.maxLength !== undefined) {
    stringSchema = stringSchema.max(
      validation.maxLength,
      validation.errorMessage,
    );
  }
  if (validation.pattern) {
    stringSchema = stringSchema.regex(
      new RegExp(validation.pattern),
      validation.patternMessage ?? validation.errorMessage,
    );
  }
  if (validation.required) {
    stringSchema = stringSchema.min(
      validation.minLength ?? 1,
      validation.errorMessage ?? 'This field is required',
    );
    return stringSchema;
  }
  return stringSchema.optional().or(z.literal(''));
}

function buildScalarFieldSchema(descriptor: FieldDescriptor): z.ZodTypeAny {
  const { validation, fieldType } = descriptor;

  switch (fieldType) {
    case 'TEXT':
    case 'EMAIL':
    case 'PHONE': {
      const base = z.string();
      return applyStringValidation(base, validation, fieldType);
    }
    case 'NUMBER': {
      let numSchema: z.ZodTypeAny = z
        .union([z.number(), z.string()])
        .transform((val) => {
          if (typeof val === 'string') {
            const num = parseFloat(val);
            return Number.isNaN(num) ? undefined : num;
          }
          return val;
        });
      if (validation.required) {
        numSchema = numSchema.refine(
          (val) => val !== undefined && val !== null,
          { message: validation.errorMessage ?? 'This field is required' },
        );
      }
      if (validation.min !== undefined) {
        numSchema = numSchema.refine(
          (val) => val === undefined || val >= validation.min!,
          { message: validation.errorMessage },
        );
      }
      if (validation.max !== undefined) {
        numSchema = numSchema.refine(
          (val) => val === undefined || val <= validation.max!,
          { message: validation.errorMessage },
        );
      }
      return numSchema;
    }
    case 'DATE': {
      let dateSchema: z.ZodTypeAny = z.string();
      if (validation.required) {
        dateSchema = (dateSchema as z.ZodString).min(
          1,
          validation.errorMessage ?? 'This field is required',
        );
      }
      if (validation.disableFutureDates) {
        dateSchema = dateSchema.refine((d) => !d || new Date(d) <= new Date(), {
          message: validation.errorMessage ?? 'Date cannot be in the future',
        });
      }
      return validation.required ? dateSchema : dateSchema.optional();
    }
    case 'SINGLE_CHOICE':
    case 'DROPDOWN': {
      if (descriptor.options?.length) {
        const values = descriptor.options.map((o) => o.value) as [
          string,
          ...string[],
        ];
        const enumSchema = z.enum(values, {
          errorMap: () => ({
            message: validation.errorMessage ?? 'Please select a valid option',
          }),
        });
        return validation.required ? enumSchema : enumSchema.optional();
      }
      if (validation.required) {
        return z
          .string()
          .min(1, validation.errorMessage ?? 'This field is required');
      }
      return z.string().optional();
    }
    case 'MULTI_SELECT':
    case 'MULTI_SELECT_CHECKBOX': {
      let arrSchema = z.array(z.string());
      if (validation.required || validation.minSelections) {
        arrSchema = arrSchema.min(
          validation.minSelections ?? 1,
          validation.errorMessage ?? 'Please select at least one option',
        );
      }
      if (validation.maxSelections !== undefined) {
        arrSchema = arrSchema.max(
          validation.maxSelections,
          validation.errorMessage,
        );
      }
      return arrSchema;
    }
    default:
      return z.unknown();
  }
}

function addCompoundFieldsToShape(
  shape: Record<string, z.ZodTypeAny>,
  descriptor: FieldDescriptor,
): void {
  if (descriptor.fieldType === 'COMPOUND_HEIGHT') {
    const ftKey = descriptor.compoundKeys?.ft ?? 'heightFt';
    const inchesKey = descriptor.compoundKeys?.inches ?? 'heightInches';
    shape[ftKey] = heightFtZod();
    shape[inchesKey] = heightInchesZod();
    return;
  }
  if (descriptor.fieldType === 'COMPOUND_WEIGHT') {
    const weightKey = descriptor.compoundKeys?.weight ?? 'weightKg';
    shape[weightKey] = weightKgZod();
  }
}

/**
 * Builds a Zod object schema from field descriptors for flat RHF forms.
 * Compound fields expand to their underlying RHF keys (heightFt, etc.).
 */
export function buildFieldGroupSchema(
  descriptors: FieldDescriptor[],
  options?: BuildFieldGroupSchemaOptions,
): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};

  descriptors.forEach((descriptor) => {
    if (
      descriptor.fieldType === 'COMPOUND_HEIGHT' ||
      descriptor.fieldType === 'COMPOUND_WEIGHT'
    ) {
      addCompoundFieldsToShape(shape, descriptor);
      return;
    }
    shape[descriptor.id] = buildScalarFieldSchema(descriptor);
  });

  let schema = z.object(shape);

  if (options?.superRefine) {
    return schema.superRefine(options.superRefine);
  }

  return schema;
}
